import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter, Href } from 'expo-router';
import { AlertCircleIcon, ArrowLeftIcon, EyeIcon, EyeOffIcon } from 'lucide-react-native';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Keyboard } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';
import { z } from 'zod';

import { AwarenessLogo } from '@/components/AwarenessLogo';
import { Header } from '@/components/header';
import PromotionalCarousel from '@/components/promotional-carousel';
import { getScreenColumnClasses } from '@/components/screenColumn';
import { SupportForm } from '@/components/supportForm';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Input, InputField, InputIcon, InputSlot } from '@/components/ui/input';
import { Link, LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import UnauthenticatedLayout from '@/components/unauthenticatedLayout';
import { useAuth } from '@/hooks/useAuth';
import { useColors } from '@/hooks/useColors';
import { useCreateAccount } from '@/hooks/useCreateAccount';
import { useRedirectPath, performRedirectNavigation } from '@/hooks/useRedirectPath';
import { useReferralNavigation } from '@/hooks/useReferralNavigation';
import { useSupportForm } from '@/hooks/useSupportForm';
import { useToaster } from '@/hooks/useToaster';
import { createUserV2 } from '@/services/createUserV2';
import {
  trackAccountConflictDetected,
  trackAccountCreateErrored,
  trackAccountCreatedUnverified,
  trackShowPasswordToggled,
  trackCreateAccountStep2BackTriggered,
  trackCreateAccountStep2Completed,
  trackCreateAccountClosed,
} from '@/util/analyticsEvents';
import { isApiErrorV2 } from '@/util/fetchClient';
import { trackGoogleAdsAccountCreatedUnverifiedConversion } from '@/util/googleAdsConversion';

const SCREEN_COLUMN = getScreenColumnClasses({ gutter: 'px-5 web:px-0' });

const formSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must include an uppercase letter')
      .regex(/[a-z]/, 'Password must include a lowercase letter')
      .regex(/[0-9]/, 'Password must include a number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

/**
 * iOS password rules for automatic password generation
 *
 * Enforces minimum 8 characters with at least one uppercase letter, one
 * lowercase letter, and one digit.
 * Used by iOS to suggest compliant passwords when users tap the password field.
 */
const passwordRules = 'minlength: 8; required: lower; required: upper; required: digit;';

/**
 * Create Account – Step 2 (Password)
 * Responsibilities:
 * - Validate password/confirmation and create account
 * - Login after creation, then redirect to NPI verification
 * - Track creation, errors, and show/hide toggles
 */
function Content() {
  const { data } = useCreateAccount();

  const { navigate, replace } = useRouter();

  const toast = useToaster();

  const { login } = useAuth();

  const initialEmail = data?.email || '';

  const hasEmail = initialEmail.trim().length > 0;

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    resolver: zodResolver(formSchema),
  });

  const passwordRef = useRef<React.ComponentProps<typeof InputField>>(null);

  const confirmPasswordRef = useRef<React.ComponentProps<typeof InputField>>(null);

  const { mutateAsync: createAccountMutation } = useMutation({
    mutationFn: createUserV2,
    meta: {
      slowWarningTitle: 'Hang tight',
      slowWarningMessage: 'Creating account, please wait',
    },
  });

  const [showPassword, setShowPassword] = useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [showSupportModal, setShowSupportModal] = useState(false);

  const supportForm = useSupportForm({ initialValues: { email: initialEmail } });

  const onSubmit = handleSubmit(async (formData) => {
    if (!hasEmail) {
      toast.showError({ title: 'Email address is required' });

      return;
    }

    try {
      trackCreateAccountStep2Completed({ email: initialEmail });

      const result = await createAccountMutation({
        email: initialEmail,
        password: formData.password,
        ...(data?.referredBy ? { referredBy: data.referredBy } : {}),
      });

      trackAccountCreatedUnverified({
        email: initialEmail,
        userId: result?.id?.toString(),
        referredByName: result?.referredByName || data?.referredBy || undefined,
        referredBy: data?.referredBy,
      });

      trackGoogleAdsAccountCreatedUnverifiedConversion();

      await login({
        email: initialEmail,
        password: formData.password,
      });

      const params = new URLSearchParams();

      if (data?.referredBy) {
        params.set('referred_by', data.referredBy);
      }

      if (redirectPath) {
        params.set('redirect_path', redirectPath);
      }

      const queryString = params.toString();

      const npiVerificationPath = queryString
        ? `/(secure)/npi-verification?${queryString}`
        : '/(secure)/npi-verification';

      replace(npiVerificationPath as Href);
    } catch (error) {
      const isEmailInUse =
        isApiErrorV2(error) && error.body.error.includes('E-mail already in use');

      if (isEmailInUse) {
        trackAccountConflictDetected({
          conflictType: 'EMAIL_IN_USE',
          authProvider: 'password',
          signupMethod: 'password',
          source: 'create_account_step_2',
          email: initialEmail,
        });
      } else if (isApiErrorV2(error)) {
        trackAccountCreateErrored({
          email: initialEmail,
          npiNumber: undefined,
          error,
        });
      } else if (error instanceof Error) {
        trackAccountCreateErrored({
          email: initialEmail,
          npiNumber: undefined,
          error,
        });
      }

      toast.showError({
        title: 'Could not complete request',
        apiError: {
          error,
          errorPatternMatcher: {
            "Last Name doesn't match NPI": "Last name doesn't match NPI number",
            'E-mail already in use': 'This email is already in use',
          },
        },
      });
    }
  });

  const { colors } = useColors();

  const { bottom } = useSafeAreaInsets();

  const { getNavigationPath } = useReferralNavigation();

  const { redirectPath, appendRedirectPath } = useRedirectPath();

  return (
    <VStack
      className={twJoin('flex-1', SCREEN_COLUMN.frame)}
      style={{
        paddingBottom: bottom,
      }}>
      {isWeb ? null : (
        <Header
          contentScrollsUnderHeader
          isBackButton
          title="Create Account"
          animation="fade"
          statusBarBackgroundColor={colors.background[0]}
        />
      )}

      <KeyboardAwareScrollView
        contentInsetAdjustmentBehavior="automatic"
        mode="layout"
        bottomOffset={32}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={false}
        contentContainerClassName={twJoin(
          'flex-col flex-grow pt-6 pb-6 web:pb-10',
          SCREEN_COLUMN.content
        )}>
        {isWeb ? (
          <HStack className="mb-8 items-center gap-x-2">
            <Button
              variant="link"
              onPress={() => {
                trackCreateAccountStep2BackTriggered();

                navigate(getNavigationPath('/create-account/step-1'));
              }}>
              <Icon as={ArrowLeftIcon} />
            </Button>

            <Text size="sm" className="font-medium text-typography-900">
              Step 2 of 2
            </Text>
          </HStack>
        ) : null}

        {!hasEmail ? (
          <Text size="xs" className="text-typography-error mb-4">
            Email is required. Please go back and enter your email.
          </Text>
        ) : null}

        <VStack className="items-center web:items-start">
          <AwarenessLogo
            alt="Primum logo with mark"
            kind="dark2x"
            box={{ height: 35, width: 280, marginBottom: 8 }}
          />
          <Text size="md" className="mb-6 mt-6 text-center text-typography-900 web:text-left">
            Please set your password to continue.
          </Text>
        </VStack>

        <VStack className="mb-4 web:mb-6" space="md">
          <Controller
            name="password"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FormControl isInvalid={!!errors.password?.message}>
                <FormControlLabel className="mb-1.5">
                  <FormControlLabelText size="sm">Password*</FormControlLabelText>
                </FormControlLabel>

                <Input>
                  <InputField
                    autoCorrect={false}
                    autoCapitalize="none"
                    ref={passwordRef}
                    style={{ color: colors.typography[900] }}
                    placeholder="Enter password"
                    placeholderTextColor={colors.typography[600]}
                    className="text-base"
                    value={value}
                    onChangeText={onChange}
                    type={showPassword ? 'text' : 'password'}
                    passwordRules={passwordRules}
                    enterKeyHint="next"
                    returnKeyType="next"
                    onSubmitEditing={() => {
                      if (isWeb) {
                        return onSubmit();
                      }

                      // @ts-expect-error gluestack input ref typing does not expose focus
                      confirmPasswordRef.current?.focus();
                    }}
                  />

                  <InputSlot
                    className="pr-3"
                    onPress={() => {
                      setShowPassword((prev) => !prev);

                      trackShowPasswordToggled({
                        isVisible: !showPassword,
                        field: 'password',
                      });
                    }}>
                    <InputIcon as={showPassword ? EyeIcon : EyeOffIcon} />
                  </InputSlot>
                </Input>

                <FormControlError className="mt-2">
                  <FormControlErrorIcon size="xs" as={AlertCircleIcon} />

                  <FormControlErrorText size="xs">{errors.password?.message}</FormControlErrorText>
                </FormControlError>
              </FormControl>
            )}
          />

          <Controller
            name="confirmPassword"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FormControl isInvalid={!!errors.confirmPassword?.message}>
                <FormControlLabel className="mb-1.5">
                  <FormControlLabelText size="sm">Re-enter password*</FormControlLabelText>
                </FormControlLabel>

                <Input>
                  <InputField
                    autoCorrect={false}
                    autoCapitalize="none"
                    ref={confirmPasswordRef}
                    style={{ color: colors.typography[900] }}
                    placeholder="Enter password"
                    placeholderTextColor={colors.typography[600]}
                    className="text-base"
                    value={value}
                    onChangeText={onChange}
                    type={showConfirmPassword ? 'text' : 'password'}
                    passwordRules={passwordRules}
                    enterKeyHint="next"
                    returnKeyType="next"
                    blurOnSubmit
                    onSubmitEditing={() => {
                      if (isWeb) {
                        return onSubmit();
                      }

                      Keyboard.dismiss();
                    }}
                  />

                  <InputSlot
                    className="pr-3"
                    onPress={() => {
                      setShowConfirmPassword((prev) => !prev);

                      trackShowPasswordToggled({
                        isVisible: !showConfirmPassword,
                        field: 'confirmPassword',
                      });
                    }}>
                    <InputIcon as={showConfirmPassword ? EyeIcon : EyeOffIcon} />
                  </InputSlot>
                </Input>

                <FormControlError className="mt-2">
                  <FormControlErrorIcon size="xs" as={AlertCircleIcon} />

                  <FormControlErrorText size="xs">
                    {errors.confirmPassword?.message}
                  </FormControlErrorText>
                </FormControlError>
              </FormControl>
            )}
          />
        </VStack>

        <Text size="xs" className="text-typography-700">
          Password requirements:
        </Text>
        <Text size="xs" className="text-typography-700">
          • Must be a Minimum 8 Characters
        </Text>
        <Text size="xs" className="text-typography-700">
          • Must Include an uppercase letter
        </Text>
        <Text size="xs" className="text-typography-700">
          • Must include a lowercase letter
        </Text>
        <Text size="xs" className="text-typography-700">
          • Must include a number
        </Text>

        <VStack className="flex-1" />

        <Button
          size="xl"
          className="w-full gap-x-2 web:mt-6"
          onPress={onSubmit}
          disabled={isSubmitting || !hasEmail}>
          {isSubmitting ? <ButtonSpinner color={colors.primary[200]} /> : null}
          <ButtonText size="md">Continue</ButtonText>
        </Button>

        <HStack className="mt-4 flex-wrap justify-start">
          <Text size="sm" className="text-typography-900">
            Already have an account?{' '}
          </Text>

          <Link onPress={() => navigate(appendRedirectPath('/(open)/login'))}>
            <LinkText
              size="sm"
              className="font-bold text-primary-500 data-[hover=true]:text-primary-600">
              Sign in
            </LinkText>
          </Link>
        </HStack>

        <SupportForm
          isOpen={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          initialValues={{
            ...supportForm.initialValues,
            reason: 'Sign Up',
          }}
        />
      </KeyboardAwareScrollView>
    </VStack>
  );
}

export default function Step2() {
  const { replace } = useRouter();

  const { redirectPath } = useRedirectPath();

  if (isWeb) {
    return (
      <UnauthenticatedLayout
        verticalAligned
        rightContent={<PromotionalCarousel onDark />}
        onBack={() => {
          trackCreateAccountClosed({ step: 'step2' });

          performRedirectNavigation(redirectPath, '/directory', replace);
        }}>
        <Content />
      </UnauthenticatedLayout>
    );
  }

  return <Content />;
}
